import { INVOICE_PERIOD_OPTIONS } from './invoicePeriodOptions'
import { InvoiceStatus } from '../types'

export type IssueDateSort = 'asc' | 'desc'

export type InvoiceListPersistedFilters = {
  status?: string[]
  clientId?: string
  period?: string
  from?: string
  to?: string
  issueDateSort?: IssueDateSort
}

const GLOBAL_STORAGE_KEY = 'tinyinvoice.invoiceList.filters'

const VALID_STATUSES = new Set<string>(Object.values(InvoiceStatus))
const VALID_PERIODS = new Set<string>(
  INVOICE_PERIOD_OPTIONS.map((opt) => opt.value).filter((value) => value !== 'all'),
)

const getStorageKey = (fixedClientId?: string): string =>
  fixedClientId ? `${GLOBAL_STORAGE_KEY}.client.${fixedClientId}` : GLOBAL_STORAGE_KEY

export const getDefaultInvoiceListFilters = (
  fixedClientId?: string,
): InvoiceListPersistedFilters & { status: string[] } => {
  const status = fixedClientId
    ? [InvoiceStatus.DRAFT, InvoiceStatus.OPEN, InvoiceStatus.OVERDUE, InvoiceStatus.PAID]
    : [InvoiceStatus.DRAFT, InvoiceStatus.OPEN, InvoiceStatus.OVERDUE]

  const filters: InvoiceListPersistedFilters & { status: string[] } = { status }
  if (fixedClientId) {
    filters.clientId = fixedClientId
  }
  return filters
}

const isValidDateString = (value: unknown): value is string =>
  typeof value === 'string' && value.trim() !== '' && !Number.isNaN(Date.parse(value))

const parseStatus = (value: unknown): string[] | undefined => {
  if (!Array.isArray(value)) return undefined
  const statuses = value.filter((item): item is string => typeof item === 'string' && VALID_STATUSES.has(item))
  return statuses.length > 0 ? statuses : undefined
}

const parsePersistedFilters = (raw: unknown): InvoiceListPersistedFilters | null => {
  if (!raw || typeof raw !== 'object') return null

  const data = raw as Record<string, unknown>
  const parsed: InvoiceListPersistedFilters = {}

  const status = parseStatus(data.status)
  if (status) parsed.status = status

  if (typeof data.clientId === 'string' && data.clientId.trim() !== '') {
    parsed.clientId = data.clientId
  }

  if (typeof data.period === 'string' && VALID_PERIODS.has(data.period)) {
    parsed.period = data.period
  }

  if (isValidDateString(data.from)) parsed.from = data.from
  if (isValidDateString(data.to)) parsed.to = data.to

  if (data.issueDateSort === 'asc' || data.issueDateSort === 'desc') {
    parsed.issueDateSort = data.issueDateSort
  }

  return Object.keys(parsed).length > 0 ? parsed : null
}

export const loadInvoiceListFilters = (fixedClientId?: string): InvoiceListPersistedFilters | null => {
  if (typeof window === 'undefined') return null
  try {
    const raw = window.localStorage.getItem(getStorageKey(fixedClientId))
    if (!raw) return null
    const parsed = parsePersistedFilters(JSON.parse(raw))
    if (!parsed) return null
    if (fixedClientId) {
      delete parsed.clientId
    }
    return parsed
  } catch {
    return null
  }
}

const arraysEqual = (a: string[], b: string[]): boolean =>
  a.length === b.length && a.every((value, index) => value === b[index])

const toPersistedPayload = (
  filters: InvoiceListPersistedFilters,
  fixedClientId?: string,
): InvoiceListPersistedFilters => {
  const defaults = getDefaultInvoiceListFilters(fixedClientId)
  const payload: InvoiceListPersistedFilters = {}

  if (filters.status && filters.status.length > 0 && !arraysEqual(filters.status, defaults.status)) {
    payload.status = filters.status
  }

  if (!fixedClientId && filters.clientId && filters.clientId.trim() !== '') {
    payload.clientId = filters.clientId
  }

  if (filters.from && filters.to) {
    payload.from = filters.from
    payload.to = filters.to
  } else if (filters.period && filters.period !== 'all') {
    payload.period = filters.period
  }

  if (filters.issueDateSort && filters.issueDateSort !== 'asc') {
    payload.issueDateSort = filters.issueDateSort
  }

  return payload
}

export const saveInvoiceListFilters = (
  filters: InvoiceListPersistedFilters,
  fixedClientId?: string,
): void => {
  if (typeof window === 'undefined') return
  try {
    const payload = toPersistedPayload(filters, fixedClientId)
    const key = getStorageKey(fixedClientId)
    if (Object.keys(payload).length === 0) {
      window.localStorage.removeItem(key)
      return
    }
    window.localStorage.setItem(key, JSON.stringify(payload))
  } catch {
    // ignore quota / private mode errors
  }
}

export const clearInvoiceListFilters = (fixedClientId?: string): void => {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.removeItem(getStorageKey(fixedClientId))
  } catch {
    // ignore storage errors
  }
}

export const filtersDifferFromDefaults = (
  filters: InvoiceListPersistedFilters & { status: string[] },
  issueDateSort: IssueDateSort,
  fixedClientId?: string,
): boolean => {
  const defaults = getDefaultInvoiceListFilters(fixedClientId)

  if (!arraysEqual(filters.status, defaults.status)) return true
  if (issueDateSort !== 'asc') return true

  if (!fixedClientId && filters.clientId) return true
  if (filters.period || filters.from || filters.to) return true

  return false
}
